import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail as fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { RCA_SEVERITY } from "@/lib/rca";

export default function CreateRcaDialog({ ticket, onClose }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState(ticket ? `RCA: ${ticket.title}` : "");
  const [clientId, setClientId] = useState(ticket?.client_id || "");
  const [engineerId, setEngineerId] = useState(ticket?.assignee_id || "");
  const [severity, setSeverity] = useState(ticket?.priority === "urgent" ? "SEV1" : ticket?.priority === "high" ? "SEV2" : "SEV3");
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const clients = users.filter((u) => u.role === "client");
  const staff = users.filter((u) => u.role !== "client");

  const submit = async () => {
    if (!title.trim() || !clientId || !engineerId) { toast.error("Title, client and responsible engineer are required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/rcas", { title, client_id: clientId, engineer_id: engineerId, severity, ticket_id: ticket?.id || null });
      toast.success(`${data.key} created`);
      onClose();
      navigate(`/rca/${data.id}`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-rca-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">New RCA</DialogTitle>
          <DialogDescription>{ticket ? `Linked to ${ticket.key}. ` : ""}You'll fill in the full analysis on the next screen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2"><Label>Incident title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Checkout API outage" data-testid="rca-title-input" /></div>
          <div className="space-y-2">
            <Label>Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="rca-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Responsible engineer</Label>
              <Select value={engineerId} onValueChange={setEngineerId}>
                <SelectTrigger data-testid="rca-engineer-select"><SelectValue placeholder="Select engineer" /></SelectTrigger>
                <SelectContent>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid="rca-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(RCA_SEVERITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="rca-create-submit">{loading ? "Creating…" : "Create RCA"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
